import * as React from 'react';
import { Banner, BannerProps, Button } from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';

import { useAdminAlertPreview } from '@app/Admin/Alerts/AdminAlertPreviewContext';

import { useActiveAlert } from './useActiveAlert';

/** Maps DB `level` to PatternFly v5 Banner `variant`. */
export const LEVEL_TO_VARIANT: Record<string, BannerProps['variant']> = {
  info: 'blue',
  warning: 'gold',
  danger: 'red',
  success: 'green',
};

export function bannerVariant(level: string): BannerProps['variant'] {
  return LEVEL_TO_VARIANT[level] ?? 'default';
}

type AlertBannerProps = {
  alert: { message: string; link: string | null; level: string };
  onDismiss?: () => void;
  isPreview?: boolean;
};

function AlertBanner({ alert, onDismiss, isPreview = false }: AlertBannerProps) {
  const variant = bannerVariant(alert.level);
  const link = alert.link;

  return (
    <Banner
      isSticky
      variant={variant}
      screenReaderText={
        isPreview
          ? `Preview alert, not published: ${alert.level}. ${alert.message}`
          : `${alert.level} alert`
      }
      className={`devex-alert-banner${isPreview ? ' devex-alert-banner--preview' : ''}`}
      {...(isPreview ? { role: 'region', 'aria-label': 'Alert preview, not published' } : {})}
    >
      <div className={`devex-alert-banner__inner${isPreview ? ' devex-alert-banner__inner--preview-top' : ''}`}>
        {link ? (
          <a
            className={`devex-alert-banner__link-overlay${isPreview ? ' devex-alert-banner__link-overlay--preview' : ''}`}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={alert.message}
          />
        ) : null}
        {isPreview ? (
          <div className="devex-alert-banner__preview-marks" aria-hidden="true">
            <span className="devex-alert-banner__preview-mark devex-alert-banner__preview-mark--start">PREVIEW</span>
            <span className="devex-alert-banner__preview-mark devex-alert-banner__preview-mark--end">PREVIEW</span>
          </div>
        ) : null}
        <div
          className={
            link
              ? 'devex-alert-banner__grid devex-alert-banner__grid--has-link-overlay'
              : 'devex-alert-banner__grid'
          }
        >
          <div aria-hidden="true" />
          <div className="devex-alert-banner__message" aria-hidden={!!link}>
            {alert.message}
          </div>
          <div className="devex-alert-banner__actions">
            <Button
              variant="plain"
              size="sm"
              onClick={onDismiss}
              aria-label={isPreview ? 'Dismiss preview' : 'Dismiss alert'}
              icon={<TimesIcon />}
            />
          </div>
        </div>
      </div>
    </Banner>
  );
}

const ActiveAlertBanner: React.FunctionComponent = () => {
  const { previewAlert, dismissPreview } = useAdminAlertPreview();
  const { alert, dismiss } = useActiveAlert();

  if (previewAlert) {
    return <AlertBanner alert={previewAlert} onDismiss={dismissPreview} isPreview />;
  }

  if (!alert) {
    return null;
  }

  return <AlertBanner alert={alert} onDismiss={dismiss} />;
};

export { ActiveAlertBanner };
